

import { useMemo } from 'react';
import { Input } from '@repo/ui/components/ui/input';
import { Textarea } from '@repo/ui/components/ui/textarea';
import { Checkbox } from '@repo/ui/components/ui/checkbox';
import { Label } from '@repo/ui/components/ui/label';
import ReactSelect from 'react-select';
import { FungusSelect } from '@repo/ui/features/inventaires/arbre/edit/components/fungus-select';
import { ParasiteSelect } from '@repo/ui/features/inventaires/arbre/edit/components/parasite-select';


interface FieldRendererProps {
  fieldKey: string;
  fieldConfig: any;
  value: any;
  error?: string;
  onChange: (key: string, value: any) => void;
  formData: Record<string, any>;
  champignons?: any[];
  nuisibles?: any[];
}

export function EpaysageFieldRenderer({
  fieldKey,
  fieldConfig,
  value,
  error,
  onChange,
  formData,
  champignons = [],
  nuisibles = [],
}: FieldRendererProps) {
  const widget = fieldConfig.widget || 'Input';
  const label = fieldConfig.label || fieldKey;
  const isRequired = !!fieldConfig.required;

  const resolvedValue = useMemo(() => {
    if (value === undefined || value === null) {
      if (widget === 'List' && fieldConfig.type === 'multiple') {
        return [];
      }
      return '';
    }
    return value;
  }, [value, widget, fieldConfig]);

  const handleChange = (newValue: any) => {
    onChange(fieldKey, newValue);
  };

  // Render freeforms for this field
  const renderFreeforms = () => {
    if (!fieldConfig?.freeforms) return null;

    const currentValue = formData[fieldKey];
    const currentValues = Array.isArray(currentValue)
      ? currentValue
      : [currentValue].filter(Boolean);

    return (
      <div className="space-y-3 mt-3">
        {fieldConfig.freeforms.map((ff: any) => {
          const ffServerKey = ff.server || `${fieldKey}_${ff.id}`;
          const ffValue = formData[ffServerKey] || '';
          const ffType = (ff.type || 'String').toLowerCase();
          const ffLabel = ff.label || 'Autre, précisez :';

          // Show freeform input if the corresponding option is selected OR if it already has a value
          const isSelected = Array.isArray(currentValues) ? currentValues.includes(ff.id) : currentValues === ff.id;
          const hasValue = ffValue && (typeof ffValue === 'string' ? ffValue.trim() !== '' : Array.isArray(ffValue) ? ffValue.length > 0 : true);
          if (!isSelected && !hasValue) return null;

          if (ffType === 'integer') {
            return (
              <div key={ff.id} className="space-y-2">
                <Label className="text-sm font-medium">{ffLabel}</Label>
                <Input
                  type="number"
                  value={ffValue || ''}
                  onChange={(e) => onChange(ffServerKey, e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Entrer un nombre"
                />
              </div>
            );
          }

          if (ffType === 'array' && ff.constraint === 'Fungus') {
            return (
              <FungusSelect
                key={ff.id}
                champignons={champignons || []}
                value={Array.isArray(ffValue) ? ffValue as number[] : []}
                onChange={(val: number[]) => onChange(ffServerKey, val)}
                label={ffLabel}
                fieldId={`${fieldKey}-${ff.id}`}
                placeholder="Sélectionnez des champignons..."
              />
            );
          }

          if (ffType === 'array' && ff.constraint === 'Parasites') {
            return (
              <ParasiteSelect
                key={ff.id}
                nuisibles={nuisibles || []}
                value={Array.isArray(ffValue) ? ffValue as number[] : []}
                onChange={(val: number[]) => onChange(ffServerKey, val)}
                label={ffLabel}
                fieldId={`${fieldKey}-${ff.id}`}
                placeholder="Sélectionnez des nuisibles..."
              />
            );
          }

          return (
            <div key={ff.id} className="space-y-2">
              <Label className="text-sm font-medium">{ffLabel}</Label>
              <Input
                type="text"
                value={ffValue || ''}
                onChange={(e) => onChange(ffServerKey, e.target.value)}
                placeholder={ffType === 'array' ? 'Entrez des valeurs séparées par des virgules' : 'Saisir ici'}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-muted-foreground flex justify-between">
        <span>{label}</span>
        {isRequired && <span className="text-xs text-destructive">*</span>}
      </Label>
      {renderInput(widget, fieldConfig, resolvedValue, handleChange)}
      {renderFreeforms()}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function renderInput(
  widget: string,
  config: any,
  value: any,
  onChange: (value: any) => void
) {

  if (widget === 'Textarea') {
    return (
      <Textarea
        value={value || ''}
        placeholder={config.placeholder || config.label}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-[120px]"
      />
    );
  }

  if (widget === 'List' && config.type === 'multiple') {
    const values = Array.isArray(value) ? value : [];

    // Create options for ReactSelect including freeforms
    const selectOptions = [
      ...(config.values || []).map((option: any) => ({
        value: option.id,
        label: option.label,
      })),
      ...(config.freeforms || []).map((ff: any) => ({
        value: ff.id,
        label: ff.label || 'Autre, précisez :',
      })),
    ];

    // Convert current values to react-select format (array of option objects)
    const selectedOptions = selectOptions.filter((opt: any) => values.includes(opt.value));

    return (
      <ReactSelect<{ value: string; label: string }, true>
        options={selectOptions}
        value={selectedOptions}
        onChange={(newValues) => {
          const selectedIds = (newValues as { value: string; label: string }[] || []).map(opt => opt.value);
          onChange(selectedIds);
        }}
        placeholder={config.placeholder || 'Sélectionnez...'}
        isMulti={true}
        isSearchable={true}
        isClearable={true}
        className="react-select-container"
        classNamePrefix="react-select"
        styles={{
          control: (base: any) => ({
            ...base,
            minHeight: '40px',
            border: '2px solid hsl(var(--input))',
            '&:hover': {
              border: '2px solid hsl(var(--primary) / 0.2)',
            },
          }),
          placeholder: (base: any) => ({
            ...base,
            color: 'hsl(var(--muted-foreground))',
          }),
        }}
      />
    );
  }

  if (widget === 'List') {
    // Create options for ReactSelect including freeforms
    const selectOptions = [
      ...(config.values || []).map((option: any) => ({
        value: option.id,
        label: option.label,
      })),
      ...(config.freeforms || []).map((ff: any) => ({
        value: ff.id,
        label: ff.label || 'Autre, précisez :',
      })),
    ];

    // Find selected option
    const selectedOption = selectOptions.find((opt: any) => opt.value == value) || null;

    return (
      <ReactSelect<{ value: string; label: string }, false>
        options={selectOptions}
        value={selectedOption}
        onChange={(newValue) => onChange(newValue?.value || '')}
        placeholder={config.placeholder || 'Sélectionner'}
        isSearchable={true}
        isClearable={true}
        className="react-select-container"
        classNamePrefix="react-select"
        styles={{
          control: (base: any) => ({
            ...base,
            minHeight: '40px',
            border: '2px solid hsl(var(--input))',
            '&:hover': {
              border: '2px solid hsl(var(--primary) / 0.2)',
            },
          }),
          placeholder: (base: any) => ({
            ...base,
            color: 'hsl(var(--muted-foreground))',
          }),
        }}
      />
    );
  }

  if (widget === 'Checkbox') {
    return (
      <div className="flex items-center space-x-2">
        <Checkbox checked={!!value} onCheckedChange={(checked) => onChange(!!checked)} />
        <span className="text-sm">{config.description}</span>
      </div>
    );
  }

  return (
    <Input
      value={value || ''}
      placeholder={config.placeholder || config.label}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

